<template>
  <div class="admin-dashboard">
    <hc-content class="admin-dashboard__section" :items="statItems" :loading="loading" />

    <div class="admin-dashboard__grid-2 admin-dashboard__section">
      <el-card shadow="never" class="admin-card">
        <template #header>
          <span class="admin-dashboard__section-title">{{ t('dashboard.stats') }}</span>
        </template>
        <dashboard-chart :stats="statItems" />
      </el-card>
      <el-card shadow="never" class="admin-card">
        <template #header>
          <span class="admin-dashboard__section-title">{{ t('dashboard.quickLinks') }}</span>
        </template>
        <div class="quick-links">
          <div
            v-for="link in quickLinks"
            :key="link.path"
            class="quick-link"
            @click="router.push(link.path)"
          >
            <el-icon :size="18"><component :is="link.icon" /></el-icon>
            <span>{{ link.label }}</span>
          </div>
        </div>
      </el-card>
    </div>

    <workbench-today class="admin-dashboard__section" />

    <el-card v-if="showMaintenanceSection" shadow="never" class="admin-card admin-dashboard__section admin-dashboard__legacy-todo">
      <template #header>
        <div class="dashboard-todo-header">
          <span class="admin-dashboard__section-title">{{ t('dashboard.maintenanceAudit') }}</span>
          <el-button type="primary" link @click="router.push('/equipment/maintenance')">{{ t('viewMore') }}</el-button>
        </div>
      </template>
      <hc-warning />
    </el-card>

    <div v-if="showTaskSections" class="admin-dashboard__grid-2 admin-dashboard__section admin-dashboard__legacy-todo">
      <el-card v-if="showSpecialSection" shadow="never" class="admin-card">
        <template #header>
          <div class="dashboard-todo-header">
            <span class="admin-dashboard__section-title">{{ t('dashboard.specialAudit') }}</span>
            <el-button type="primary" link @click="router.push('/task/submitRecord')">{{ t('viewMore') }}</el-button>
          </div>
        </template>
        <hc-special-table />
      </el-card>
      <el-card v-if="showImproveSection" shadow="never" class="admin-card">
        <template #header>
          <div class="dashboard-todo-header">
            <span class="admin-dashboard__section-title">{{ t('dashboard.improveAudit') }}</span>
            <el-button type="primary" link @click="router.push('/improve/submitRecord')">{{ t('viewMore') }}</el-button>
          </div>
        </template>
        <hc-improve-table />
      </el-card>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { List, User, Setting, Clock } from '@element-plus/icons-vue'
import { getHomeDataApi } from '@/api/home/home'
import hcContent from './components/contnet-item.vue'
import hcWarning from './components/warning.vue'
import hcSpecialTable from './components/special-table.vue'
import hcImproveTable from './components/improve-table.vue'
import DashboardChart from './components/dashboard-chart.vue'
import WorkbenchToday from './components/workbench-today.vue'
import { usePageI18n } from '@/composables/usePageI18n.js'
import { HOME_STAT_KEY_ORDER } from '@/utils/homeDashboardI18n.js'
import { isPathAllowedForUser } from '@/utils/adminMenuPermission.js'

const router = useRouter()
const statItems = ref([])
const loading = ref(true)
const allQuickLinks = ref([])

const showMaintenanceSection = computed(() => isPathAllowedForUser('/equipment/maintenance'))
const showSpecialSection = computed(() => isPathAllowedForUser('/task/submitRecord'))
const showImproveSection = computed(() => isPathAllowedForUser('/improve/submitRecord'))
const showTaskSections = computed(() => showSpecialSection.value || showImproveSection.value)

const statIconMap = {
  jishouMun: require('@/assets/home/jishouMun.png'),
  captainMun: require('@/assets/home/jishouMun.png'),
  sheibeiMun: require('@/assets/home/sheibeiMun.png'),
  baoyangMun: require('@/assets/home/baoyangMun.png'),
  teshuMun: require('@/assets/home/teshuMun.png'),
  jinnegMun: require('@/assets/home/jinnegMun.png'),
  userMun: require('@/assets/home/userMun.png'),
}

const applyLabels = () => {
  allQuickLinks.value = [
    { label: t('dashboardHome.quickAttendance'), path: '/attendance', icon: Clock },
    { label: t('dashboardHome.quickUsers'), path: '/system', icon: User },
    { label: t('dashboardHome.quickDevice'), path: '/equipment/device', icon: Setting },
    { label: t('dashboardHome.quickMaintenance'), path: '/equipment', icon: List },
  ]
}
const { t } = usePageI18n(applyLabels)

const quickLinks = computed(() => allQuickLinks.value.filter((link) => isPathAllowedForUser(link.path)))

const loadHomeStats = () => {
  loading.value = true
  getHomeDataApi('{}').then((res) => {
    const data = res.data || {}
    statItems.value = HOME_STAT_KEY_ORDER.map((key) => ({
      key,
      title: key,
      num: data[key] ?? 0,
      imgUrl: statIconMap[key] || statIconMap.userMun,
    }))
  }).finally(() => {
    loading.value = false
  })
}

onMounted(() => {
  applyLabels()
  loadHomeStats()
})
</script>

<style lang="less" scoped>
.admin-dashboard {
  height: 100%;
  overflow: auto;
}

.admin-dashboard__legacy-todo {
  display: none;
}

.admin-dashboard__section {
  margin-bottom: var(--admin-gap-lg);
}

.admin-dashboard__section-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--admin-text-primary);
}

.dashboard-todo-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.quick-links {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.quick-link {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  border-radius: var(--admin-radius-md);
  background: #f8fafc;
  border: 1px solid #eef2f6;
  cursor: pointer;
  font-size: 14px;
  color: #334155;
  transition: background 0.15s, border-color 0.15s;

  &:hover {
    background: #f1f5f9;
    border-color: #e2e8f0;
    color: var(--admin-primary);
  }
}

:deep(.el-card__header) {
  padding: 14px 20px;
  border-bottom: 1px solid var(--admin-card-border);
}

:deep(.el-card__body) {
  padding: 16px 20px;
}
</style>
